import { pb } from '@/libs/pocketbase'
import { tryCatch } from '@/utils/try-catch'
import type {
	Department,
	DepartmentPort,
	EmergencyContact,
	MemberPort,
	OrgTeam,
	OrgTeamPort,
	TeamMember,
} from '../domain/member'

type ListResult<T> = { items: T[] } | null

const toQuery = (params: Record<string, string | null | undefined>): string => {
	const sp = new URLSearchParams()
	for (const [k, v] of Object.entries(params)) {
		if (v !== null && v !== undefined && v !== '') sp.set(k, v)
	}
	const s = sp.toString()
	return s ? `?${s}` : ''
}

const withAbsoluteAvatar = (member: TeamMember): TeamMember =>
	member.avatarUrl && member.avatarUrl.startsWith('/')
		? { ...member, avatarUrl: `${pb.baseURL}${member.avatarUrl}` }
		: member

// Translates a 404 result into null to satisfy port contracts typed as T | null.
// Any other failure is re-thrown so application-layer error handling still fires.
const orNullOn404 = async <T>(request: Promise<T>): Promise<T | null> => {
	const result = await tryCatch(request)
	if (!result.isSuccess) {
		if (result.error.code === 404) return null
		throw new Error(result.error.message)
	}
	return result.data
}

const pbMembers: MemberPort = {
	list: async filter => {
		const raw = await pb.send<ListResult<TeamMember> | TeamMember[]>(`/api/hr/members${toQuery({ q: filter.q, status: filter.status })}`, {
			method: 'GET',
		})
		const members = Array.isArray(raw) ? raw : (raw?.items ?? [])
		return members.map(withAbsoluteAvatar)
	},

	getById: async id => {
		const member = await orNullOn404(pb.send<TeamMember>(`/api/hr/members/${id}`, { method: 'GET' }))
		return member ? withAbsoluteAvatar(member) : null
	},

	create: async data => {
		const member = await pb.send<TeamMember>('/api/hr/members', {
			method: 'POST',
			body: JSON.stringify(data),
			headers: { 'Content-Type': 'application/json' },
		})
		return withAbsoluteAvatar(member)
	},

	update: async (id, data) => {
		const member = await pb.send<TeamMember>(`/api/hr/members/${id}`, {
			method: 'PATCH',
			body: JSON.stringify(data),
			headers: { 'Content-Type': 'application/json' },
		})
		return withAbsoluteAvatar(member)
	},

	setAvatar: async (id, file) => {
		const form = new FormData()
		form.append('avatar', file)
		form.append('mimeType', file.type)
		const member = await pb.send<TeamMember>(`/api/hr/members/${id}/avatar`, { method: 'POST', body: form })
		return withAbsoluteAvatar(member)
	},

	archive: id => pb.send<void>(`/api/hr/members/${id}/archive`, { method: 'POST' }),

	addEmergencyContact: (memberId, contact) =>
		pb.send<EmergencyContact>(`/api/hr/members/${memberId}/emergency-contacts`, {
			method: 'POST',
			body: JSON.stringify(contact),
			headers: { 'Content-Type': 'application/json' },
		}),

	removeEmergencyContact: (memberId, contactId) =>
		pb.send<void>(`/api/hr/members/${memberId}/emergency-contacts/${contactId}`, {
			method: 'DELETE',
		}),
}

const pbDepartments: DepartmentPort = {
	listDepartments: () => pb.send<Department[]>('/api/hr/departments', { method: 'GET' }),

	getDepartmentById: id => orNullOn404(pb.send<Department>(`/api/hr/departments/${id}`, { method: 'GET' })),

	createDepartment: data =>
		pb.send<Department>('/api/hr/departments', {
			method: 'POST',
			body: JSON.stringify(data),
			headers: { 'Content-Type': 'application/json' },
		}),
}

const pbOrgTeams: OrgTeamPort = {
	listTeams: () => pb.send<OrgTeam[]>('/api/hr/teams', { method: 'GET' }),

	getTeamById: id => orNullOn404(pb.send<OrgTeam>(`/api/hr/teams/${id}`, { method: 'GET' })),

	createTeam: data =>
		pb.send<OrgTeam>('/api/hr/teams', {
			method: 'POST',
			body: JSON.stringify(data),
			headers: { 'Content-Type': 'application/json' },
		}),

	updateTeam: (id, data) =>
		pb.send<OrgTeam>(`/api/hr/teams/${id}`, {
			method: 'PATCH',
			body: JSON.stringify(data),
			headers: { 'Content-Type': 'application/json' },
		}),

	deleteTeam: id => pb.send<void>(`/api/hr/teams/${id}`, { method: 'DELETE' }),

	addMemberToTeam: (teamId, memberId) =>
		pb.send<void>(`/api/hr/teams/${teamId}/members/${memberId}`, { method: 'POST' }),

	removeMemberFromTeam: (teamId, memberId) =>
		pb.send<void>(`/api/hr/teams/${teamId}/members/${memberId}`, { method: 'DELETE' }),

	addLeaderToTeam: (teamId, memberId) =>
		pb.send<void>(`/api/hr/teams/${teamId}/leaders/${memberId}`, { method: 'POST' }),

	removeLeaderFromTeam: (teamId, memberId) =>
		pb.send<void>(`/api/hr/teams/${teamId}/leaders/${memberId}`, { method: 'DELETE' }),
}

export { pbMembers, pbDepartments, pbOrgTeams }
